import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowDown, ArrowUp, ArrowUpDown, Camera, ClipboardCheck, Plus } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ScoreNumber, SeverityChip, StatusChip } from '../components/evidence';
import { inspectionStage, scoreBand } from '../lib/damage';

import { API } from '../lib/api';

const INSPECTION_TYPES = [
  { value: 'move_in', label: 'Move-in', hint: 'Record the condition before a tenant moves in.' },
  { value: 'move_out', label: 'Move-out', hint: "Find new damage and decide what's chargeable." },
  { value: 'routine', label: 'Routine', hint: 'A periodic check during a tenancy.' },
  { value: 'maintenance', label: 'Maintenance', hint: 'Document a repair or a reported issue.' },
];

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'progress', label: 'In progress' },
  { key: 'review', label: 'Needs review' },
  { key: 'approved', label: 'Approved' },
];

function formatDate(value) {
  if (!value) return '--';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '--' : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function typeLabel(type) {
  return INSPECTION_TYPES.find((t) => t.value === type)?.label || 'Inspection';
}

function roomCount(insp) {
  return insp.room_analyses?.length || insp.room_count || 0;
}

function StageChip({ insp }) {
  const stage = inspectionStage(insp);
  if (stage === 'review') return <StatusChip tone="review">Needs review</StatusChip>;
  if (stage === 'approved') return <StatusChip done>Approved</StatusChip>;
  return <StatusChip>In progress</StatusChip>;
}

const th = 'whitespace-nowrap border-b border-line px-4 py-3 text-xs font-bold uppercase leading-4 tracking-[0.08em] text-ink-muted';
const td = 'border-b border-line px-4 py-3.5 align-middle';

export default function InspectionsPage() {
  const [inspections, setInspections] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedProp, setSelectedProp] = useState('');
  const [inspType, setInspType] = useState('move_out');
  const [creating, setCreating] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
  const navigate = useNavigate();

  useEffect(() => {
    if (dialogOpen && properties.length > 0 && !selectedProp) {
      setSelectedProp(properties[0].id);
    }
  }, [dialogOpen, properties, selectedProp]);

  useEffect(() => {
    const load = async () => {
      try {
        const [inspRes, propRes] = await Promise.all([
          axios.get(`${API}/inspections`),
          axios.get(`${API}/properties`),
        ]);
        setInspections(inspRes.data);
        setProperties(propRes.data);
      } catch {
        toast.error("Couldn't load your inspections. Refresh to try again.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const createInspection = async (e) => {
    e?.preventDefault();
    if (!selectedProp) { toast.error('Choose a property first.'); return; }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/inspections`, { property_id: selectedProp, inspection_type: inspType });
      toast.success('Inspection started');
      setDialogOpen(false);
      navigate(`/inspections/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't start the inspection. Try again.");
    } finally {
      setCreating(false);
    }
  };

  const counts = useMemo(() => {
    const c = { all: inspections.length, progress: 0, review: 0, approved: 0 };
    inspections.forEach((i) => { c[inspectionStage(i)] += 1; });
    return c;
  }, [inspections]);

  const displayedInspections = useMemo(() => {
    const filtered = inspections.filter((insp) => activeFilter === 'all' || inspectionStage(insp) === activeFilter);
    const value = (insp) => {
      switch (sortConfig.key) {
        case 'date': return new Date(insp.completed_at || insp.created_at || 0).getTime();
        case 'property': return String(insp.property_name || '');
        case 'type': return typeLabel(insp.inspection_type);
        case 'rooms': return roomCount(insp);
        case 'score': return insp.overall_score ?? -1;
        case 'status': return inspectionStage(insp);
        default: return String(insp.property_name || '');
      }
    };
    return [...filtered].sort((a, b) => {
      const av = value(a);
      const bv = value(b);
      const comp = typeof av === 'string' ? av.localeCompare(String(bv)) : Number(av) - Number(bv);
      return sortConfig.direction === 'asc' ? comp : -comp;
    });
  }, [inspections, activeFilter, sortConfig]);

  const setSort = (key) => {
    setSortConfig((prev) => (
      prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: key === 'date' || key === 'score' ? 'desc' : 'asc' }
    ));
  };

  const SortHeader = ({ sortKey, children, align = 'left' }) => {
    const active = sortConfig.key === sortKey;
    const Icon = !active ? ArrowUpDown : sortConfig.direction === 'asc' ? ArrowUp : ArrowDown;
    return (
      <th
        scope="col"
        className={`${th} ${align === 'right' ? 'text-right' : ''}`}
        aria-sort={active ? (sortConfig.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
      >
        <button
          type="button"
          onClick={() => setSort(sortKey)}
          className={`inline-flex items-center gap-1 uppercase tracking-[0.08em] ${active ? 'text-ink' : 'text-ink-muted hover:text-ink'}`}
        >
          {children}
          <Icon className="h-3.5 w-3.5" strokeWidth={2} aria-hidden="true" />
        </button>
      </th>
    );
  };

  return (
    <DashboardLayout>
      <div data-testid="inspections-page" className="flex flex-col gap-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="m-0 font-display text-3xl font-bold leading-9 tracking-[-0.01em]" data-testid="inspections-heading">Inspections</h1>
            <p className="m-0 mt-1 text-ink-muted">
              {inspections.length} {inspections.length === 1 ? 'inspection' : 'inspections'} across {properties.length} {properties.length === 1 ? 'property' : 'properties'}
            </p>
          </div>
          <Button onClick={() => setDialogOpen(true)} data-testid="new-inspection-dialog-btn">
            <Plus strokeWidth={1.5} />New inspection
          </Button>
        </div>

        <div role="tablist" aria-label="Filter inspections" className="flex flex-wrap self-start overflow-hidden rounded-md border border-line-strong">
          {FILTERS.map((tab, i) => {
            const selected = activeFilter === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={selected}
                onClick={() => setActiveFilter(tab.key)}
                className={`h-10 px-4 text-sm font-bold transition-colors ${i > 0 ? 'border-l border-line-strong' : ''} ${
                  selected ? 'bg-ink text-paper' : 'bg-surface text-ink hover:bg-sunken'
                }`}
                data-testid={`inspection-filter-${tab.key}`}
              >
                {tab.label} <span className={`font-mono ${selected ? '' : 'text-ink-muted'}`}>{counts[tab.key]}</span>
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="h-[320px] rounded-md bg-sunken" aria-busy="true" aria-label="Loading inspections" />
        ) : displayedInspections.length === 0 ? (
          <div className="flex flex-col items-start gap-3 rounded-md border border-dashed border-line-strong bg-surface p-8">
            <ClipboardCheck className="h-6 w-6 text-ink-muted" strokeWidth={1.5} aria-hidden="true" />
            <h2 className="m-0 font-display text-[22px] font-bold leading-7">
              {inspections.length === 0 ? 'Your first inspection' : 'Nothing in this filter'}
            </h2>
            <p className="m-0 max-w-[46ch] text-ink-muted">
              {inspections.length === 0
                ? 'Start one at move-in so you have something to compare at move-out.'
                : 'Pick another filter to see the rest of your inspections.'}
            </p>
            {inspections.length === 0 && (
              <Button onClick={() => setDialogOpen(true)} data-testid="empty-new-inspection"><Plus strokeWidth={1.5} />New inspection</Button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto rounded-md border border-line bg-surface">
            <table className="w-full min-w-[860px] border-collapse text-left text-[15px] leading-[22px]">
              <thead>
                <tr>
                  <SortHeader sortKey="date">Date</SortHeader>
                  <SortHeader sortKey="property">Property</SortHeader>
                  <SortHeader sortKey="type">Type</SortHeader>
                  <SortHeader sortKey="rooms" align="right">Rooms</SortHeader>
                  <SortHeader sortKey="score">Damage score</SortHeader>
                  <SortHeader sortKey="status">Status</SortHeader>
                  <th scope="col" className={th}><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {displayedInspections.map((insp) => (
                  <tr key={insp.id} className="hover:bg-sunken" data-testid={`inspection-item-${insp.id}`}>
                    <td className={`${td} whitespace-nowrap font-mono text-sm font-semibold`}>{formatDate(insp.completed_at || insp.created_at)}</td>
                    <td className={`${td} min-w-[220px]`}>
                      <b className="block">{insp.property_name}</b>
                      <span className="block text-sm text-ink-muted">{insp.property_address}</span>
                    </td>
                    <td className={td}>{typeLabel(insp.inspection_type)}</td>
                    <td className={`${td} text-right font-mono font-semibold tabular`}>{roomCount(insp)}</td>
                    <td className={td}>
                      {scoreBand(insp.overall_score) ? (
                        <span className="inline-flex items-center gap-2.5">
                          <ScoreNumber score={insp.overall_score} />
                          <SeverityChip score={insp.overall_score} />
                        </span>
                      ) : (
                        <span className="text-ink-muted"><span className="font-mono font-semibold">--</span> No photos yet</span>
                      )}
                    </td>
                    <td className={td}><StageChip insp={insp} /></td>
                    <td className={`${td} text-right`}>
                      <Button asChild variant="outline" size="sm" data-testid={`inspection-open-${insp.id}`}>
                        <Link to={`/inspections/${insp.id}`}>Open</Link>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-[600px]">
          <DialogHeader>
            <DialogTitle>New inspection</DialogTitle>
            <DialogDescription>You'll photograph each room on the next screen.</DialogDescription>
          </DialogHeader>
          {properties.length === 0 ? (
            <div className="flex flex-col items-start gap-3">
              <p className="m-0">An inspection belongs to a property. Add one first, then come back here.</p>
              <Button onClick={() => { setDialogOpen(false); navigate('/properties'); }}>Go to properties</Button>
            </div>
          ) : (
            <form onSubmit={createInspection} className="flex flex-col gap-5">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="inspection-property">Property</Label>
                <Select value={selectedProp} onValueChange={setSelectedProp}>
                  <SelectTrigger id="inspection-property" data-testid="inspection-property-select">
                    <SelectValue placeholder="Choose a property" />
                  </SelectTrigger>
                  <SelectContent>
                    {properties.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name}{p.address ? `, ${p.address}` : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <fieldset className="m-0 flex flex-col gap-2 border-0 p-0" data-testid="inspection-type-select">
                <legend className="mb-2 p-0 text-sm font-bold leading-5">Inspection type</legend>
                <div className="grid gap-2.5 sm:grid-cols-2">
                  {INSPECTION_TYPES.map((t) => {
                    const checked = inspType === t.value;
                    return (
                      <label
                        key={t.value}
                        className={`flex cursor-pointer items-start gap-3 rounded-md p-3.5 transition-colors has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-ink ${
                          checked ? 'border-2 border-ink bg-marker-soft p-[13px]' : 'border border-line-strong hover:bg-sunken'
                        }`}
                      >
                        <input
                          type="radio"
                          name="inspection-type"
                          value={t.value}
                          checked={checked}
                          onChange={() => setInspType(t.value)}
                          className="sr-only"
                        />
                        <span
                          aria-hidden="true"
                          className={`mt-[3px] h-[18px] w-[18px] shrink-0 rounded-full ${checked ? 'border-[5px] border-ink bg-surface' : 'border-2 border-line-strong'}`}
                        />
                        <span>
                          <b className="block">{t.label}</b>
                          <span className={`text-sm leading-5 ${checked ? 'text-ink' : 'text-ink-muted'}`}>{t.hint}</span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              </fieldset>

              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={creating || !selectedProp} data-testid="create-inspection-submit">
                  <Camera strokeWidth={1.5} />{creating ? 'Starting…' : 'Start inspection'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
